import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, X, Activity, ClipboardEdit, Send, ShieldAlert, TrendingUp, Save,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import { PageHead } from "@/components/AppLayout";
import { Card, Btn, Badge, Avatar, EmptyState, statusTone, SearchInput } from "@/components/ui-kit";
import api from "@/lib/api";

const FILTERS = ["all", "On Track", "Review Needed", "At Risk", "Completed"];

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [editNote, setEditNote] = useState(false);
  const [noteVal, setNoteVal] = useState("");

  const load = useCallback(() => api.get("/patients").then((r) => setPatients(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const shown = patients.filter((p) =>
    (filter === "all" || p.status === filter) &&
    (!q || p.name.toLowerCase().includes(q.toLowerCase()))
  );
  const count = (f) => f === "all" ? patients.length : patients.filter((p) => p.status === f).length;

  const withCare = patients.filter((p) => p.total_weeks);
  const adh = { high: 0, med: 0, low: 0 };
  withCare.forEach((p) => { const a = p.adherence || 0; if (a >= 80) adh.high++; else if (a >= 50) adh.med++; else adh.low++; });
  const avgAdh = withCare.length ? Math.round(withCare.reduce((s, p) => s + (p.adherence || 0), 0) / withCare.length) : 0;
  const rec = {
    onTrack: patients.filter((p) => p.status === "On Track" || p.status === "Completed").length,
    review: patients.filter((p) => p.status === "Review Needed").length,
    risk: patients.filter((p) => p.status === "At Risk").length,
  };

  const openSel = (p) => { setSel(p); setEditNote(false); setNoteVal(p.note || ""); };

  const act = async (kind) => {
    if (!sel) return;
    if (kind === "continue") { await api.put(`/patients/${sel.id}`, { status: "On Track" }); toast.success(`Care plan continued for ${sel.name}`); }
    if (kind === "review") { await api.put(`/patients/${sel.id}`, { status: "Review Needed" }); toast.success(`Review requested for ${sel.name}`); }
    load();
  };
  const saveNote = async () => {
    await api.put(`/patients/${sel.id}`, { note: noteVal });
    toast.success("Restrictions & notes updated");
    setSel({ ...sel, note: noteVal }); setEditNote(false); load();
  };

  const trend = (sel?.pain_trend || []).map((v, i) => ({ i, v }));

  return (
    <>
      <PageHead title="My Patients" subtitle="Track rehabilitation progress, identify risks, and take timely action." />

      {/* Overview widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        <Card className="p-5" data-testid="adherence-overview">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display font-semibold text-[14px] flex items-center gap-2"><Activity size={16} className="text-teal-600" /> Adherence Overview</h3><span className="metric text-lg font-extrabold">{avgAdh}%</span></div>
          {[["High (≥80%)", adh.high, "bg-emerald-500"], ["Medium (50–79%)", adh.med, "bg-amber-500"], ["Low (<50%)", adh.low, "bg-rose-500"]].map(([label, n, c]) => {
            const pct = withCare.length ? Math.round((n / withCare.length) * 100) : 0;
            return (
              <div key={label} className="mb-2">
                <div className="flex justify-between text-[12px] text-slate-600 mb-1"><span>{label}</span><span className="font-semibold">{n}</span></div>
                <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${c}`} style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </Card>
        <Card className="p-5" data-testid="recovery-overview">
          <h3 className="font-display font-semibold text-[14px] flex items-center gap-2 mb-3"><TrendingUp size={16} className="text-teal-600" /> Recovery Overview</h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-emerald-50 rounded-xl py-3"><div className="metric text-2xl font-extrabold text-emerald-600">{rec.onTrack}</div><div className="text-[11.5px] text-slate-500 mt-0.5">On track</div></div>
            <div className="bg-amber-50 rounded-xl py-3"><div className="metric text-2xl font-extrabold text-amber-600">{rec.review}</div><div className="text-[11.5px] text-slate-500 mt-0.5">Needs review</div></div>
            <div className="bg-rose-50 rounded-xl py-3"><div className="metric text-2xl font-extrabold text-rose-600">{rec.risk}</div><div className="text-[11.5px] text-slate-500 mt-0.5">At risk</div></div>
          </div>
        </Card>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`patient-filter-${f.replace(/\s/g, "-")}`} onClick={() => setFilter(f)}
              className={`pq-chip ${filter === f ? "pq-chip-active" : ""}`}>
              {f === "all" ? "All" : f} <span className="opacity-70">{count(f)}</span>
            </button>
          ))}
        </div>
        <SearchInput testid="patient-search-input" value={q} onChange={setQ} placeholder="Search patients…" />
      </div>

      <div className={`grid gap-5 items-start [&>*]:min-w-0 ${sel ? "grid-cols-1 xl:grid-cols-[2fr_1fr]" : "grid-cols-1"}`}>
        <Card>
          {shown.length === 0 ? (
            <EmptyState icon={<Users size={24} />} title="No patients yet"
              subtitle="Patients appear here once you create a treatment order, or add them from the New Treatment Order flow." />
          ) : (
            <div className="overflow-x-auto"><table className="w-full">
              <thead><tr>{["Patient", "Diagnosis", "Week", "Progress", "Adherence", "Next Review", "Status"].map((h) => (
                <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100">{h}</th>))}</tr></thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} onClick={() => openSel(p)} data-testid={`patient-row-${p.id}`}
                    className={`border-b border-slate-100 last:border-0 cursor-pointer ${sel?.id === p.id ? "bg-teal-50" : "hover:bg-slate-50"}`}>
                    <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Avatar name={p.name} size={34} /><div><div className="font-semibold text-[13.5px]">{p.name}</div><div className="text-[11.5px] text-slate-500">{p.age} · {p.gender}</div></div></div></td>
                    <td className="px-4 py-3 text-[13px]">{p.diagnosis || "—"}</td>
                    <td className="px-4 py-3 text-[13px] whitespace-nowrap">{p.total_weeks ? `Wk ${p.current_week}/${p.total_weeks}` : "—"}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${p.progress}%` }} /></div><span className="text-[12px]">{p.progress}%</span></div></td>
                    <td className="px-4 py-3"><span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${p.adherence >= 80 ? "bg-emerald-500" : p.adherence >= 50 ? "bg-amber-500" : "bg-rose-500"}`} />{p.adherence}%</td>
                    <td className="px-4 py-3 text-[13px]">{p.next_review || "—"}</td>
                    <td className="px-4 py-3"><Badge tone={statusTone(p.status)}>{p.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </Card>

        {sel && (
          <Card className="p-5 sticky top-24 animate-fade-in" data-testid="patient-detail-panel">
            <button className="float-right text-slate-400 hover:text-slate-700" onClick={() => setSel(null)} data-testid="close-patient-panel"><X size={18} /></button>
            <div className="flex items-center gap-3"><Avatar name={sel.name} size={44} /><div><div className="font-display font-bold text-[15px]">{sel.name}</div><div className="text-[12px] text-slate-500">{sel.age} · {sel.gender} · {sel.pranit_id}</div></div></div>
            <div className="mt-3 text-[13px] text-slate-600">{sel.diagnosis || "No active diagnosis"}</div>
            <div className="mt-2"><Badge tone={statusTone(sel.status)}>{sel.status}</Badge></div>

            <h4 className="font-display font-semibold text-[13px] mt-5 mb-1 flex items-center gap-2"><Activity size={15} className="text-teal-600" /> Recovery Progress</h4>
            <div className="flex items-center justify-between text-[11.5px] text-slate-500"><span>Week {sel.current_week} of {sel.total_weeks || "—"}</span><span>{sel.progress}% complete</span></div>
            <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden mt-1.5"><div className="h-full bg-teal-600" style={{ width: `${sel.progress}%` }} /></div>

            {trend.length > 0 && (<>
              <h4 className="font-display font-semibold text-[13px] mt-5 mb-1">Pain / Symptom Trend</h4>
              <div className="h-20"><ResponsiveContainer width="100%" height="100%"><LineChart data={trend}><YAxis hide domain={[0, 10]} /><Line type="monotone" dataKey="v" stroke="#1A3A6D" strokeWidth={2.5} dot={{ r: 3, fill: "#DAA520" }} /></LineChart></ResponsiveContainer></div>
              <div className="text-[11px] text-slate-400 -mt-1">Latest pain score: <b className="text-slate-700">{sel.pain_trend[sel.pain_trend.length - 1]}/10</b></div>
            </>)}

            <div className="mt-4 bg-slate-50 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[12px] font-semibold">Restrictions & Notes</div>
                {!editNote && <button className="text-[11.5px] font-semibold text-teal-700 flex items-center gap-1" onClick={() => { setEditNote(true); setNoteVal(sel.note || ""); }} data-testid="modify-restrictions-btn"><ClipboardEdit size={13} /> Modify</button>}
              </div>
              {editNote ? (
                <>
                  <textarea className="pq-input min-h-[80px] text-[12px]" value={noteVal} onChange={(e) => setNoteVal(e.target.value)} placeholder="Update restrictions, precautions or clinical notes…" data-testid="restrictions-textarea" />
                  <div className="flex gap-2 mt-2"><Btn size="sm" variant="primary" onClick={saveNote} data-testid="save-restrictions-btn"><Save size={13} /> Save</Btn><Btn size="sm" onClick={() => setEditNote(false)}>Cancel</Btn></div>
                </>
              ) : (
                <div className="text-[12px] text-slate-600 leading-relaxed">{sel.note || "No therapist notes recorded yet for this patient."}</div>
              )}
            </div>

            <Btn variant="primary" className="w-full mt-4" data-testid="patient-continue-btn" onClick={() => act("continue")}><Send size={15} /> Continue Care</Btn>
            <Btn className="w-full mt-2" data-testid="patient-request-review-btn" onClick={() => act("review")}><ShieldAlert size={15} /> Request Review</Btn>
          </Card>
        )}
      </div>
    </>
  );
}
